import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { requestWithCookieAuth } from "api";
import { updateDeliverable } from "../../api";
import { PROJECT_IQ_API_BASE } from "config";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import { IDeliverable } from "models/IProjectIQWorkspace";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import {
  DELIVERABLES_QUERY_KEY,
  STATUS_LABELS,
} from "./DeliverablesPage.types";
import {
  DEFAULT_FORM_VALUES,
  IDeliverableFormDialogProps,
  IFormValues,
} from "./DeliverableFormDialog.types";

const createDeliverable = async (
  workspaceId: string,
  data: Omit<IDeliverable, "id" | "createdAt" | "updatedAt">,
): Promise<IDeliverable> => {
  const response: any = await requestWithCookieAuth(
    `${PROJECT_IQ_API_BASE}/workspaces/${workspaceId}/deliverables/`,
    {
      method: "POST",
      redirect: "follow",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    },
  );
  if (response?.error)
    throw new Error(response.message || "Failed to create deliverable");
  return response.json();
};

export const DeliverableFormDialog = ({
  isOpen,
  deliverable,
  workspaceId,
  onDismiss,
}: IDeliverableFormDialogProps): JSX.Element => {
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceId) ?? null;

  const [formValues, setFormValues] =
    useState<IFormValues>(DEFAULT_FORM_VALUES);
  const [isNameTouched, setIsNameTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsNameTouched(false);
      if (deliverable) {
        setFormValues({
          name: deliverable.name,
          description: deliverable.description ?? "",
          dueDate: deliverable.dueDate ?? "",
          status: deliverable.status,
          owner: deliverable.owner
            ? { key: deliverable.owner, text: deliverable.owner }
            : null,
          reviewer: deliverable.reviewer
            ? { key: deliverable.reviewer, text: deliverable.reviewer }
            : null,
        });
      } else {
        setFormValues(DEFAULT_FORM_VALUES);
      }
    }
  }, [isOpen, deliverable]);

  const createMutation = useMutation(
    (data: Omit<IDeliverable, "id" | "createdAt" | "updatedAt">) =>
      createDeliverable(workspaceId, data),
    {
      onSuccess: (created) => {
        queryClient.invalidateQueries([DELIVERABLES_QUERY_KEY, workspaceId]);
        if (workspace) {
          dispatch(
            updateWorkspaceAction({
              ...workspace,
              deliverables: [...(workspace.deliverables ?? []), created],
            }),
          );
        }
        onDismiss(true);
      },
    },
  );

  const updateMutation = useMutation(
    ({
      deliverableId,
      data,
    }: {
      deliverableId: string;
      data: Partial<Omit<IDeliverable, "id" | "createdAt" | "updatedAt">>;
    }) => updateDeliverable(workspaceId, deliverableId, data),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries([DELIVERABLES_QUERY_KEY, workspaceId]);
        if (workspace) {
          dispatch(
            updateWorkspaceAction({
              ...workspace,
              deliverables: (workspace.deliverables ?? []).map((d) =>
                d.id === updated.id ? updated : d,
              ),
            }),
          );
        }
        onDismiss(true);
      },
    },
  );

  const handleFormChange = useCallback(
    (field: keyof IFormValues, value: string) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handlePersonChange = useCallback(
    (field: "owner" | "reviewer", selectedOptions: CustomOption[]) => {
      const selected =
        selectedOptions.length > 1
          ? selectedOptions[selectedOptions.length - 1]
          : (selectedOptions[0] ?? null);
      setFormValues((prev) => ({ ...prev, [field]: selected }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    const payload = {
      name: formValues.name.trim(),
      description: formValues.description.trim() || undefined,
      dueDate: formValues.dueDate || undefined,
      status: formValues.status,
      owner: formValues.owner?.text?.trim() || null,
      reviewer: formValues.reviewer?.text?.trim() || null,
    };
    if (deliverable) {
      updateMutation.mutate({
        deliverableId: deliverable.id,
        data: { ...payload, isDraftedFromSow: false },
      });
    } else {
      createMutation.mutate(payload);
    }
  }, [formValues, deliverable, createMutation, updateMutation]);

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const isSaveDisabled = isSaving || !formValues.name.trim();
  const dialogTitle = deliverable ? "Edit Deliverable" : "Add Deliverable";

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss(false);
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={() => onDismiss(false)}
              />
            }
          >
            {dialogTitle}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              <Field
                label="Name"
                required
                validationState={
                  isNameTouched && !formValues.name.trim() ? "error" : undefined
                }
                validationMessage={
                  isNameTouched && !formValues.name.trim()
                    ? "Name is required"
                    : undefined
                }
              >
                <Input
                  value={formValues.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  onBlur={() => setIsNameTouched(true)}
                  placeholder="Deliverable name"
                  autoFocus
                  disabled={isSaving}
                />
              </Field>
              <Field label="Description">
                <Input
                  value={formValues.description}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  placeholder="Brief description"
                  disabled={isSaving}
                />
              </Field>
              <Field label="Due date">
                <Input
                  type="date"
                  value={formValues.dueDate}
                  onChange={(e) => handleFormChange("dueDate", e.target.value)}
                  disabled={isSaving}
                />
              </Field>
              <Field label="Status">
                <Dropdown
                  value={STATUS_LABELS[formValues.status]}
                  selectedOptions={[formValues.status]}
                  onOptionSelect={(_e, d) =>
                    handleFormChange("status", d.optionValue ?? "not_started")
                  }
                  disabled={isSaving}
                >
                  <Option value="not_started">Not Started</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="done">Done</Option>
                </Dropdown>
              </Field>
              <Field label="Owner">
                <PeoplePicker
                  selectedOptions={formValues.owner ? [formValues.owner] : []}
                  handleSelectedItems={(opts) =>
                    handlePersonChange("owner", opts)
                  }
                  hasOnlyOurUsers
                  disabled={isSaving}
                />
              </Field>
              <Field label="Reviewer">
                <PeoplePicker
                  selectedOptions={
                    formValues.reviewer ? [formValues.reviewer] : []
                  }
                  handleSelectedItems={(opts) =>
                    handlePersonChange("reviewer", opts)
                  }
                  hasOnlyOurUsers
                  disabled={isSaving}
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip={dialogTitle}
            ariaLabel={dialogTitle}
            dimmed={isSaveDisabled}
            disabled={isSaving}
            onClick={handleSave}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
